import { XIcon } from '@heroicons/react/solid';
import { Affix, Drawer, Dropdown, Input, Spin } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import SearchIconSVG from 'assets/Icons/SearchIconSVG';
import ArrowIcon from 'assets/Icons/Shop/ArrowIcon';
import { motion } from 'framer-motion';
import { debounce } from 'lodash';
import moment from 'moment';
import { useRouter } from 'next/router';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import BookCardHome from '@/components/BundleComp/BookCard';
import SchoolFeaturedCard from '@/components/ResourcesPage/SchoolsPage/SchoolCard';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import Breadcrumbs from '@/components/shared/BreadCrunms';
import NoResultsFound from '@/components/shared/EmptyScreen';
import SkeletonCard from '@/components/shared/SkeletonCard';
import BookCard from '@/components/ShopComp/BookCard';
import BundleShopCard from '@/components/ShopComp/BundlesCards';
import CategoriesSidebarWrapper from '@/components/ShopComp/CategoriesSidebarWrapper';
import CoursesCard from '@/components/ShopComp/CoursesCard';

import { Endpoints } from '@/network';
import { generateUrlForImage } from '@/pages/books/[bookId]';
import { getBlogPagination } from '@/services/blog';
import { getAllCourse } from '@/services/Courses';
import { getAllBookListApi } from '@/services/Library/Books';
import { getAllBundleListApi } from '@/services/Library/Bundle';
import { getAllOrangesBookListApi } from '@/services/Library/Oranges';
import { getAllProducts } from '@/services/Products';
import type { IBookShop } from '@/utils/interfaces/BooksShop';

interface BooksListProps {
  setSearchKeyword: (value: string) => void;
  searchKeyword: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type: 'books' | 'courses' | 'schools' | 'oranges' | 'bundles';
  ecomProducts?: any[];
}

const priceItems = [
  { key: 'all', label: 'All' },
  { key: 'free', label: 'Free' },
  { key: 'paid', label: 'Paid' },
];

const originalSortItems = [
  { key: 'default', label: 'Default' },
  { key: 'newest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'bestseller', label: 'Bestseller' },
  { key: 'highestRating', label: 'Highest Rating' },
];

const BooksList = ({
  handleInputChange,
  searchKeyword,
  setSearchKeyword,
  type,
  ecomProducts,
}: BooksListProps) => {
  const [sortBy, setSortBy] = useState<string>('default');
  const [priceFilter, setPriceFilter] = useState<string>('all');
  const [page, setPage] = useState<number>(1);
  const [products, setProducts] = useState<IBookShop[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [priceDrawerOpen, setPriceDrawerOpen] = useState(false);
  const [sortDrawerOpen, setSortDrawerOpen] = useState(false);
  const screens = useBreakpoint();
  const router = useRouter();

  const currentCategoryRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
    rootMargin: '200px 0px',
  });

  const openPriceDrawer = () => setPriceDrawerOpen(!priceDrawerOpen);
  const closePriceDrawer = () => setPriceDrawerOpen(!priceDrawerOpen);
  const openSortDrawer = () => setSortDrawerOpen(!sortDrawerOpen);
  const closeSortDrawer = () => setSortDrawerOpen(!sortDrawerOpen);

  const categoryName =
    router.asPath?.split('?')[0]?.split('/').filter(Boolean).at(-1) ?? null;

  const getSortItems = (categoryName: string | null) => {
    if (categoryName === 'free-only') {
      return originalSortItems.filter(
        (_, index) => index !== originalSortItems.length - 2,
      );
    }
    return originalSortItems;
  };

  const sortItems = getSortItems(categoryName);

  const handlePriceSelect = (key: string) => {
    setPriceFilter(key);
    closePriceDrawer();
  };

  const handleSortSelect = (key: string) => {
    setSortBy(key);
    closeSortDrawer();
  };

  useEffect(() => {
    if (!router.isReady) return;

    const timeoutId = setTimeout(() => {
      const sortParam =
        typeof router.query.sort === 'string' ? router.query.sort : 'default';
      const priceParam =
        typeof router.query.price === 'string' ? router.query.price : 'all';

      setSortBy(sortParam);
      setPriceFilter(priceParam);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [router.isReady, router.query.sort, router.query.price]);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const formatBundleData = (rawData: any[]): IBookShop[] => {
    if (!rawData || !Array.isArray(rawData)) return [];

    return rawData.map((bundle) => {
      const hardCopyPrice = bundle?.main_price || 0;
      const originalPrice = bundle?.stroked_price || 0;

      return {
        id: bundle?.id || '',
        title: bundle?.name || '',
        author: bundle?.author || 'Unknown',
        coverImage: bundle?.thumbnail_image || '',
        rating: bundle?.rating || 0,
        reviews: bundle?.reviews_count || 0,
        category: bundle?.category || '',
        price: hardCopyPrice,
        originalPrice: originalPrice,
        discount:
          originalPrice && hardCopyPrice
            ? Math.round(
              ((originalPrice - hardCopyPrice) / originalPrice) * 100,
            )
            : 0,
        tag: bundle?.tag || '',
        isFree: hardCopyPrice === 0,
        externalLink: bundle?.external_link || '',
        slug: bundle?.slug || '',
        type: bundle?.type || '',
        products: bundle?.bundle_products,
        isExternalLink: bundle?.is_external_link || 'false',
        hardCopyId: bundle?.id,
        currency: bundle?.currency_symbol || '',
        is_available: bundle?.is_available && bundle?.i18n_attributes,
        i18n_attributes: bundle?.i18n_attributes || {},
      };
    });
  };
  const deSlugify = (slug: string) => slug.replace(/-/g, ' ');

  const handleGetAllBooks = useCallback(
    async (currentPage: number, isNewCategory = false) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      try {
        if (isNewCategory) {
          setInitialLoading(true);
        } else {
          setLoading(true);
        }

        const filters: {
          page: string;
          categoriesId?: string | null;
          keyword?: string;
          limit?: string;
          type?: string;
          categoryId?: string;
          order?: string;
          featured?: boolean;
          includeCategories?: string;
          end_date?: string;
          category_name?: string;
          sortByRating?: string;
        } = {
          page: currentPage.toString(),
          keyword: searchKeyword,
        };

        let formattedProducts: IBookShop[] = [];

        currentCategoryRef.current = categoryName;

        if (categoryName === 'bundles' && type !== 'bundles') {
          const data = await getAllProducts(
            currentPage,
            7,
            undefined,
            undefined,
            currentPage === 1 ? 6 : 3,
          );

          if (currentCategoryRef.current !== categoryName) return;

          formattedProducts = formatBundleData(data?.data);
          const paginationInfo = {
            current_page: data?.meta?.current_page,
            per_page: data?.meta?.per_page,
            total: data?.meta?.total,
          };

          setHasMore(
            formattedProducts.length > 0 &&
            paginationInfo.current_page * paginationInfo.per_page <
            paginationInfo.total,
          );
        } else if (type === 'courses') {
          const response = await getAllCourse({
            page: String(currentPage),
            categoriesId:
              categoryName && categoryName !== 'shop'
                ? deSlugify(categoryName)
                : '',
            keyword: searchKeyword,
            limit: '6',
          });

          if (currentCategoryRef.current !== categoryName) return;

          const { data } = response;

          formattedProducts = data?.map((course: any) => {
            const matchedEcomCourse = ecomProducts?.find(
              (ecom: any) => ecom?.id === course?.ecom_product_id,
            );

            const is_available =
              matchedEcomCourse?.is_available &&
              matchedEcomCourse?.i18n_attributes;
            const i18n_attributes = matchedEcomCourse?.i18n_attributes;

            const currencySymbol =
              matchedEcomCourse?.currency_symbol ||
              course?.currency_symbol ||
              '₹';
            const mainPrice =
              matchedEcomCourse?.main_price || course?.discounted_price || 0;

            const strokedPrice =
              matchedEcomCourse?.stroked_price || course?.price || 0;
            return {
              _id: course?.ecom_product_id,
              id: course?.ecom_product_id,
              coverImageUrl: course?.course_image_url,
              coverImage: course?.course_image_url,
              title: course?.name,
              slug: course?.slug,
              totalPages: undefined,
              isExternalLink: false,
              externalLink: undefined,
              author_name: 'ZebraLearn',
              author: 'ZebraLearn',
              rating: course?.star_rating || 0,
              reviews: Number.parseInt(course?.student_count ?? '0'),
              category: course?.course_categories?.[0]?.category?.name,
              price: Number(mainPrice) || 0,
              originalPrice: Number(strokedPrice) || 0,
              discount: Number(strokedPrice)
                ? Math.round(
                  ((Number(strokedPrice) - Number(mainPrice)) /
                    Number(strokedPrice)) *
                  100,
                )
                : 0,

              hardCopyId: course?.ecom_product_id,
              products: [],
              isFree: Number(mainPrice) === 0,
              topTen: false,
              duration: course?.totalVideoDuration,
              currency: currencySymbol || '₹',
              is_available: is_available,
              i18n_attributes,
            };
          });
          const totalPages = Math.ceil(response?.extra?.totalDataLength / 6);
          setHasMore(currentPage < totalPages);
        } else if (type === 'schools') {
          const response = await getBlogPagination({
            page: String(currentPage),
            category_name:
              categoryName && categoryName !== 'blog'
                ? deSlugify(categoryName)
                : '',
            keyword: searchKeyword,
            limit: 6,
          });

          const totalPages = Math.ceil(response?.data?.total / 6);
          setHasMore(currentPage < totalPages);

          const rawData = response?.data?.data || [];

          formattedProducts = rawData
            .filter(
              (school: any, index: number, self: any[]) =>
                index === self.findIndex((s) => s.id === school.id),
            )
            .map((school: any) => ({
              id: school.id,
              desc: school.name,
              chapterNum: school.posts_count,
              image: school.documents?.file_path,
              slug: school?.slug,
            }));
        } else {
          const routes = [
            'shop',
            'free-only',
            'paid-only',
            'bestsellers',
            'new-releases',
          ];
          if (categoryName) {
            if (!routes.includes(categoryName)) {
              filters.category_name = deSlugify(categoryName);
            }

            const lowerCategory = categoryName.toLowerCase();

            if (lowerCategory === 'free-only') {
              filters.type = 'short';
            } else if (lowerCategory === 'paid-only') {
              filters.type = 'long,comingSoon';
            } else if (lowerCategory === 'bestsellers') {
              filters.featured = true;
            } else if (lowerCategory === 'new-releases') {
              const endDate = moment()
                .subtract(2, 'months')
                .format('YYYY-MM-DD');
              filters.end_date = endDate;
            }

            if (sortBy === 'newest') {
              filters.order = 'desc';
            } else if (sortBy === 'oldest') {
              filters.order = 'asc';
            } else if (sortBy === 'bestseller') {
              filters.featured = true;
            } else if (sortBy === 'highestRating') {
              filters.sortByRating = 'high';
            }

            if (priceFilter === 'free') {
              filters.type = 'short';
            } else if (priceFilter === 'paid') {
              filters.type = 'long,comingSoon';
            } else if (sortBy === 'paid') {
              filters.order = 'long';
            }
          }

          const response =
            type === 'oranges'
              ? await getAllOrangesBookListApi(filters)
              : type === 'bundles'
                ? await getAllBundleListApi(filters)
                : await getAllBookListApi(filters);

          if (currentCategoryRef.current !== categoryName) return;

          const { data } = response;

          formattedProducts = data?.map((book: any) => {
            let tag: string | undefined;

            if (book?.type === 'comingSoon') {
              tag = 'PRE ORDER';
            } else if (
              moment(book.createdAt).isAfter(moment().subtract(2, 'months'))
            ) {
              tag = 'NEW RELEASE';
            } else if (book?.featured) {
              tag = 'BESTSELLER';
            }

            const reviews = book?.reviews || [];
            const totalReviews = reviews.length;

            const averageRating: number =
              totalReviews > 0
                ? Number.parseFloat(
                  (
                    reviews.reduce(
                      (acc: number, cur: { rating: number }) =>
                        acc + cur.rating,
                      0,
                    ) / totalReviews
                  ).toFixed(1),
                )
                : 0;

            const matchedEcomBook = ecomProducts?.find(
              (ecom: any) =>
                ecom.hardCopy?.id === book?.hardcoverProductId ||
                ecom.hardCopy?.id === book?.productId ||
                ecom.hardCopy?.id === book?.paperbackProductId ||
                ecom.digitalCopy?.id === book?.digitalProductId,
            );
            const preOrderPrice =
              matchedEcomBook?.hardCopy?.preorder_display_price;

            const is_available_hard_copy =
              matchedEcomBook?.hardCopy?.is_available &&
              matchedEcomBook?.hardCopy?.i18n_attributes;
            const is_available_digital_copy =
              matchedEcomBook?.digitalCopy?.is_available &&
              matchedEcomBook?.digitalCopy?.i18n_attributes;
            const i18n_attributes = matchedEcomBook?.hardCopy?.i18n_attributes;

            const currencySymbol = preOrderPrice
              ? matchedEcomBook?.digitalCopy?.currency_symbol
              : is_available_hard_copy
                ? matchedEcomBook?.hardCopy?.currency_symbol
                : is_available_digital_copy
                  ? matchedEcomBook?.digitalCopy?.currency_symbol
                  : book?.currency_symbol || '₹';

            const mainPrice = preOrderPrice
              ? preOrderPrice
              : is_available_hard_copy
                ? matchedEcomBook?.hardCopy?.main_price
                : is_available_digital_copy
                  ? matchedEcomBook?.digitalCopy?.main_price
                  : book?.main_price || book?.price || 0;

            const strokedPrice = preOrderPrice
              ? matchedEcomBook?.digitalCopy?.stroked_price || 0
              : is_available_hard_copy
                ? matchedEcomBook?.hardCopy?.stroked_price
                : is_available_digital_copy
                  ? matchedEcomBook?.digitalCopy?.stroked_price
                  : book?.stroked_price || book?.originalPrice || 0;

            const authorName =
              book?.author_list?.[0]?.name ||
              (Array.isArray(book?.authors) && book.authors.length > 0
                ? typeof book.authors[0] === 'string'
                  ? book.authors.join(', ')
                  : book.authors[0]?.name
                : undefined) ||
              (typeof book?.author === 'string' ? book.author : book?.author?.name) ||
              book?.author_name ||
              '';

            const isAvailable = Boolean(
              is_available_hard_copy ||
              is_available_digital_copy ||
              matchedEcomBook?.hardCopy?.is_available ||
              (Number(mainPrice) > 0 && book?.isListed !== false && book?.hardCoverAvailable !== false)
            );

            return {
              _id: book?._id,
              coverImageUrl: book?.coverImageUrl?.startsWith('http')
                ? book.coverImageUrl
                : generateUrlForImage(
                  `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
                  type === 'bundles'
                    ? `${Endpoints.getCoverImagesForBundle}/`
                    : `${Endpoints.getCoverImagesForBooks}/`,
                  `${book?._id}`,
                ),
              title: book?.title,
              externalLink: book?.externalLink,
              currency: currencySymbol,
              slug: book?.slug,
              totalPages: book?.totalPages || 0,
              isExternalLink: book?.isExternalLink,
              author_name: authorName,
              id: book?._id,
              author: authorName,
              coverImage: book?.coverImageUrl?.startsWith('http')
                ? book.coverImageUrl
                : generateUrlForImage(
                  `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
                  type === 'bundles'
                    ? `${Endpoints.getCoverImagesForBundle}/`
                    : `${Endpoints.getCoverImagesForBooks}/`,
                  `${book?._id}`,
                ),
              rating: averageRating || 0,
              reviews: totalReviews,
              category: book?.categories?.[0]?.name,
              price: Number(mainPrice),
              originalPrice: Number(strokedPrice),
              discount:
                Number(strokedPrice) && Number(strokedPrice) > Number(mainPrice)
                  ? Math.round(((Number(strokedPrice) - Number(mainPrice)) / Number(strokedPrice)) * 100)
                  : 0,
              tag,
              hardCopyId: book?.id,
              products: book?.bundle_products,
              isFree: book?.type === 'short' || Number(mainPrice) === 0,
              topTen: book.topRanking,
              is_available: isAvailable,
              i18n_attributes: i18n_attributes,
              type: book?.type || (type === 'oranges' ? 'paperBack' : type === 'bundles' ? 'bundle' : 'books'),
              bookCount:
                book?.bookCount ??
                (Array.isArray(book?.bundle_products)
                  ? book.bundle_products.filter((p: any) => p.type !== 'course').length
                  : undefined),
              courseCount:
                book?.courseCount ??
                (Array.isArray(book?.bundle_products)
                  ? book.bundle_products.filter((p: any) => p.type === 'course').length
                  : undefined),
              main_price: book?.main_price,
              stroked_price: book?.stroked_price,
            };
          });

          const paginationInfo = {
            current_page: response?.metadata?.page,
            per_page: response?.metadata?.limit,
            total: response?.metadata?.total,
          };
          setHasMore(
            formattedProducts.length > 0 &&
            paginationInfo.current_page * paginationInfo.per_page <
            paginationInfo.total,
          );
        }

        if (currentPage === 1 || isNewCategory) {
          setProducts(formattedProducts || []);
          setPage(1);
        } else {
          setProducts((prevData) => [
            ...prevData,
            ...(formattedProducts || []),
          ]);
          setPage(currentPage);
        }
      } catch (error: any) {
        console.error('Error fetching books:', error);
        setHasMore(false);
      } finally {
        setLoading(false);
        setInitialLoading(false);
        isFetchingRef.current = false;
      }
    },
    [categoryName, searchKeyword, sortBy, priceFilter, type],
  );

  useEffect(() => {
    if (categoryName && router.isReady) {
      setProducts([]);
      setHasMore(true);
      setPage(1);

      handleGetAllBooks(1, true);
    }
  }, [categoryName, router.isReady, handleGetAllBooks]);

  useEffect(() => {
    if (categoryName && router.isReady && (sortBy || priceFilter)) {
      setProducts([]);
      setHasMore(true);
      setPage(1);

      const timer = setTimeout(() => {
        handleGetAllBooks(1, true);
      }, 100);

      return () => clearTimeout(timer);
    } else {
      return;
    }
  }, [sortBy, priceFilter, handleGetAllBooks, categoryName, router.isReady]);

  useEffect(() => {
    if (inView && hasMore && !loading && !initialLoading) {
      handleGetAllBooks(page + 1);
    }
  }, [inView, hasMore, loading, initialLoading, page, handleGetAllBooks]);

  useEffect(() => {
    if (!router.isReady || !categoryName) return;

    const debounced = debounce(() => {
      setProducts([]);
      setHasMore(true);
      setPage(1);
      handleGetAllBooks(1, true);
    }, 300);

    debounced();

    return () => {
      debounced.cancel();
    };
  }, [searchKeyword, handleGetAllBooks, router.isReady, categoryName]);

  const DesktopFilters = () => {
    if (!screens.md || type === 'courses' || type === 'schools') return null;

    return (
      <div className='flex gap-x-2 lg:gap-4 items-end justify-end w-full'>
        <ShouldRender
          check={categoryName !== 'paid-only' && categoryName !== 'free-only'}
        >
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div>
              <Dropdown
                menu={{
                  items: priceItems,
                  onClick: ({ key }) => {
                    const newQuery = {
                      ...router.query,
                      price: key !== 'all' ? key : undefined,
                    };

                    router.replace(
                      {
                        pathname: router.pathname,
                        query: newQuery,
                      },
                      undefined,
                      { shallow: true },
                    );
                  },
                }}
              >
                <div className='flex items-center border gap-x-1 border-[#444] text-sm rounded-lg px-3 md:py-[9px] lg:py-1.5 cursor-pointer'>
                  <span className='text-[#9F9EA6] mr-1'>Price:</span>
                  {priceItems.find((i) => i.key === priceFilter)?.label ||
                    'All'}
                  <ArrowIcon />
                </div>
              </Dropdown>
            </div>
          </motion.div>
        </ShouldRender>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div>
            <Dropdown
              menu={{
                items: sortItems,
                onClick: ({ key }) => {
                  const newQuery = {
                    ...router.query,
                    sort: key !== 'default' ? key : undefined,
                  };

                  router.replace(
                    {
                      pathname: router.pathname,
                      query: newQuery,
                    },
                    undefined,
                    { shallow: true },
                  );
                },
              }}
            >
              <div className='flex items-center border gap-x-1 md:py-[9px] lg:py-1.5 border-[#444] text-sm rounded-lg px-3 py-1.5 cursor-pointer'>
                <span className='text-[#9F9EA6] mr-1 whitespace-nowrap'>
                  Sort by:
                </span>
                {sortItems.find((i) => i.key === sortBy)?.label || 'Default'}
                <ArrowIcon />
              </div>
            </Dropdown>
          </div>
        </motion.div>
      </div>
    );
  };

  useEffect(() => {
    document.body.style.overflow =
      priceDrawerOpen || sortDrawerOpen ? 'hidden' : 'auto';
  }, [priceDrawerOpen, sortDrawerOpen]);

  const MobileFilters = () => {
    if (screens.md) return null;

    return (
      <>
        <Affix offsetBottom={0}>
          <div className='flex w-full border-t  bg-[#121212] border-[#3C3B3E]'>
            <ShouldRender
              check={
                categoryName !== 'paid-only' && categoryName !== 'free-only'
              }
            >
              <ButtonDefault
                size={4}
                variant={ButtonVariants.UNSTYLED}
                noRadius={true}
                className='w-full py-2 h-[40px] bg-[#121212] border-r border-[#3C3B3E] text-sm font-light rounded-none'
                onClick={openPriceDrawer}
              >
                Price <ArrowIcon width='16' height='16' />
              </ButtonDefault>
            </ShouldRender>
            <ButtonDefault
              size={4}
              variant={ButtonVariants.UNSTYLED}
              noRadius={true}
              className='w-full p-2 h-[40px] bg-[#121212]  text-sm font-light'
              onClick={openSortDrawer}
            >
              Sort by <ArrowIcon width='16' height='16' />
            </ButtonDefault>
          </div>
        </Affix>

        <Drawer
          placement='bottom'
          title={undefined}
          closable={false}
          onClose={closePriceDrawer}
          open={priceDrawerOpen}
          mask={false}
          className='bg-[#121212] '
          rootClassName='customDrawerShop'
        >
          <div className='flex flex-col space-y-4 px-4 pb-16'>
            <div className='h-14 flex items-center justify-center relative'>
              <span className='font-bold text-sm'>Price</span>
              <button
                onClick={closePriceDrawer}
                className='absolute right-0 bg-[#FFFFFF1A] p-1 rounded-full'
              >
                <XIcon className='w-4 h-4' />
              </button>
            </div>
            {priceItems.map((item) => (
              <button
                key={item.key}
                className={`py-3 px-4 text-left rounded-lg ${priceFilter === item.key ? 'bg-[#222]' : ''}`}
                onClick={() => handlePriceSelect(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </Drawer>

        <Drawer
          placement='bottom'
          title={undefined}
          closable={false}
          onClose={closeSortDrawer}
          open={sortDrawerOpen}
          mask={false}
          className='bg-[#121212] '
          rootClassName='customDrawerShop'
        >
          <div className='flex flex-col space-y-4 px-4 pb-16'>
            <div className='h-14 flex items-center justify-center relative'>
              <span className='font-bold text-sm'>Sort By</span>
              <button
                onClick={closeSortDrawer}
                className='absolute right-0 bg-[#FFFFFF1A] p-1 rounded-full'
              >
                <XIcon className='w-4 h-4' />
              </button>
            </div>
            {sortItems.map((item) => (
              <button
                key={item.key}
                className={`py-3 px-4 text-left rounded-lg ${sortBy === item.key ? 'bg-[#222]' : ''}`}
                onClick={() => handleSortSelect(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </Drawer>
      </>
    );
  };
  const renderContent = () => {
    if (initialLoading) {
      return (
        <div
          className={
            type === 'bundles' || categoryName === 'bundles'
              ? 'grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6'
              : 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-4 lg:gap-x-0 gap-y-6'
          }
        >
          {Array.from({ length: 10 }).map((_, index) => (
            <SkeletonCard
              variant={
                type === 'bundles' || categoryName === 'bundles'
                  ? 'bundleShop'
                  : type === 'courses'
                    ? 'courseShop'
                    : type === 'schools'
                      ? 'schoolShop'
                      : 'shopBookCard'
              }
              key={index}
              index={index}
            />
          ))}
        </div>
      );
    }

    if (products?.length < 1 && !loading) {
      return <NoResultsFound />;
    }

    if (type === 'bundles') {
      return (
        <motion.div
          className='grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6'
          variants={container}
          initial='hidden'
          animate='show'
        >
          {products.map((bundle, index) => (
            <motion.div
              key={`${bundle.id}-${index}`}
              variants={item}
              className='flex h-full w-full'
            >
              <BookCardHome
                className='w-full'
                book={{
                  coverImage: bundle.coverImage || bundle.coverImageUrl || '',
                  id: bundle.id || bundle._id || '',
                  slug: bundle.slug,
                  title: bundle.title,
                  tag: bundle.tag,
                  topTen: bundle.topTen ? String(bundle.topTen) : undefined,
                  isFree: Boolean(bundle.isFree),
                  externalLink: bundle.externalLink,
                  isExternalLink: Boolean(bundle.isExternalLink),
                  courseCount: bundle.courseCount,
                  bookCount: bundle.bookCount,
                  main_price: bundle.price || bundle.main_price,
                  stroked_price: bundle.originalPrice || bundle.stroked_price,
                }}
              />
            </motion.div>
          ))}
        </motion.div>
      );
    }

    if (categoryName === 'bundles') {
      return (
        <motion.div
          className='grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6'
          variants={container}
          initial='hidden'
          animate='show'
        >
          {products.map((bundle, index) => (
            <motion.div key={`${bundle.id}-${index}`} variants={item}>
              <BundleShopCard bundle={bundle} />
            </motion.div>
          ))}
        </motion.div>
      );
    }

    if (type === 'courses' && categoryName !== 'bundles') {
      return (
        <motion.div
          className='grid xxxs:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6'
          variants={container}
          initial='hidden'
          animate='show'
        >
          {products.map((course, index) => (
            <motion.div key={`${course.id}-${index}`} variants={item}>
              <CoursesCard course={course} />
            </motion.div>
          ))}
        </motion.div>
      );
    }

    if (type === 'schools') {
      return (
        <motion.div
          className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-6 lg:mt-3 xl:mt-6'
          variants={container}
          initial='hidden'
          animate='show'
        >
          {products.map((book, index) => {
            return (
              <motion.div key={`${book.id}-${index}`} variants={item}>
                <SchoolFeaturedCard item={book} isCategoryPage={true} />
              </motion.div>
            );
          })}
        </motion.div>
      );
    }

    return (
      <motion.div
        className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-4 lg:gap-x-0 gap-y-6'
        variants={container}
        initial='hidden'
        animate='show'
      >
        {products.map((book, index) => (
          <motion.div key={`${book.id}-${index}`} variants={item}>
            <BookCard book={book} index={index} type={type} />
          </motion.div>
        ))}
      </motion.div>
    );
  };

  return (
    <>
      <div className='px-4 md:px-0'>
        <ShouldRender check={!screens.lg}>
          <div className='flex flex-col gap-y-2 pb-4'>
            <Breadcrumbs />
            <div className='flex justify-between'>
              <motion.h1
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className='text-xl md:text-2xl xl:text-[32px] font-semibold capitalize'
              >
                {categoryName} {type === 'bundles' ? 'Bundles' : 'Books'}
              </motion.h1>
              <ShouldRender check={screens.md}>
                <CategoriesSidebarWrapper
                  handleInputChange={handleInputChange}
                  searchKeyword={searchKeyword}
                  setSearchKeyword={setSearchKeyword}
                  type={type}
                />{' '}
              </ShouldRender>
            </div>
          </div>
        </ShouldRender>
        <div className='flex justify-between pb-6 lg:pb-6 gap-x-2'>
          <ShouldRender check={screens.lg}>
            <div className='flex flex-col gap-y-3'>
              <Breadcrumbs />
              <motion.h1
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className='text-xl md:text-2xl xl:text-[32px] whitespace-nowrap capitalize font-semibold'
              >
                {deSlugify(categoryName || '')} {type === 'oranges' ? 'Books' : type === 'bundles' ? 'Bundles' : type}
              </motion.h1>
            </div>
          </ShouldRender>
          <ShouldRender check={!screens.lg}>
            <div className='w-full flex justify-between gap-x-2'>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Input
                  size='small'
                  placeholder='Search for Books'
                  className='text-sm rounded-[10px] focus-within:bg-bgColor border-[1.5px] placeholder:text-textDarkGrayColor py-1 px-3 text-white h-10 lg:h-10 bg-bgColor customInputBlack makeBgBlack'
                  value={searchKeyword}
                  style={{
                    backgroundColor: '#000000',
                  }}
                  onChange={handleInputChange}
                  prefix={
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className='text-textLightGray'
                    >
                      <SearchIconSVG width='16' isGray={true} />
                    </motion.div>
                  }
                  suffix={
                    searchKeyword?.length ? (
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <XIcon
                          onClick={() => {
                            setSearchKeyword('');
                          }}
                          className='w-5 h-5 pr-1 text-[#A6A6A6] cursor-pointer'
                        />
                      </motion.div>
                    ) : null
                  }
                />
              </motion.div>
              <ShouldRender check={!screens.md}>
                <CategoriesSidebarWrapper
                  handleInputChange={handleInputChange}
                  searchKeyword={searchKeyword}
                  setSearchKeyword={setSearchKeyword}
                  type={type}
                />
              </ShouldRender>
            </div>
          </ShouldRender>
          <ShouldRender check={screens.md}>
            <ShouldRender check={type !== 'courses'}>
              <DesktopFilters />
            </ShouldRender>
          </ShouldRender>
        </div>

        {renderContent()}

        <div
          ref={loadMoreRef}
          className='flex justify-center items-center py-6 h-20'
        >
          {loading && <Spin size='large' />}
          {!hasMore && products?.length > 0 && (
            <p className='text-gray-400 hidden'>No more items to load</p>
          )}
        </div>
      </div>
      <MobileFilters />
    </>
  );
};

export default BooksList;
